import {DEEP_LINK} from '@assets/const/deepLink';
import {getCurrentTabInfo} from '@scripttag/helpers/actionWidgetHelpers';
import {
  getWidgetSettingsStorage,
  setWidgetSettingsStorage
} from '@scripttag/helpers/sessionHelpers';
import {onChatTool} from '@assets/helpers/eventBridge';
import extractGraphQlId from '@functions/helpers/utils/extractGraphQlId';

export default class ChattyJSService {
  constructor() {
    this.handlers = {};
    this.debug = false; // process.env.NODE_ENV === 'development';
    this.unsubscribeFromTool = null;
    this.initToolListener();
  }

  initToolListener() {
    // Listen for tool calls from the chat system
    this.unsubscribeFromTool = onChatTool(payload => {
      if (this.debug) console.log('[ChattyJS] Tool call received:', payload);

      if (payload.name === 'add_to_cart') {
        this.handleAddToCart(payload.data);
      }
    });
  }

  async handleAddToCart(lineItems) {
    if (this.debug) console.log('[ChattyJS] Handling add_to_cart:', lineItems);

    try {
      // Validate lineItems
      if (!Array.isArray(lineItems) || lineItems.length === 0) {
        throw new Error('Invalid lineItems: must be a non-empty array');
      }

      // Prepare the request data according to Shopify Cart API
      const formData = {
        items: lineItems.map(item => ({
          id: extractGraphQlId(item.variant_id),
          quantity: item.quantity
        }))
      };

      if (this.debug) console.log('[ChattyJS] Form data prepared:', formData);

      const cartAddUrl = `${window.location.origin}${window.Shopify.routes.root}cart/add.js`;

      // Make the AJAX request to Shopify Cart API
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        // Try to parse error details returned by Shopify (e.g., 422 with message/description)
        let errorPayload = null;
        try {
          errorPayload = await response.json();
          // eslint-disable-next-line no-unused-vars
        } catch (e) {
          try {
            const text = await response.text();
            if (text) errorPayload = {message: text};
            // eslint-disable-next-line no-unused-vars
          } catch (_) {
            // ignore
          }
        }

        const errorDetails = {
          status: response.status,
          message: errorPayload?.message || errorPayload?.description
        };
        const errorEvent = {success: false, details: errorDetails};
        if (this.hasHandler('onAddToCartResult')) this.call('onAddToCartResult', errorEvent);
        return errorEvent;
      }

      const result = await response.json();

      if (this.debug) console.log('[ChattyJS] Cart add successful:', result);

      // Emit success event or call success handler if available
      const successEvent = {success: true, details: result};
      if (this.hasHandler('onAddToCartResult')) this.call('onAddToCartResult', successEvent);
      return successEvent;
    } catch (error) {
      console.error('[ChattyJS] Error adding to cart:', error);

      // Emit error event or call the error handler if available
      const unknownErrorEvent = {success: false, details: {message: error.message}};
      if (this.hasHandler('onAddToCartResult')) this.call('onAddToCartResult', unknownErrorEvent);
      return unknownErrorEvent;
    }
  }

  registerHandler(name, handler) {
    if (this.debug) console.log(`[ChattyJS] Registering handler: ${name}`);
    this.handlers[name] = handler;
    return this;
  }

  unregisterHandler(name) {
    if (this.debug) console.log(`[ChattyJS] Unregistering handler: ${name}`);
    delete this.handlers[name];
    return this;
  }

  hasHandler(name) {
    return typeof this.handlers[name] === 'function';
  }

  call(handlerName, ...args) {
    if (!this.hasHandler(handlerName)) {
      console.warn(`[ChattyJS] ${handlerName} handler not registered`);
      return {success: false, error: 'Handler not available'};
    }

    try {
      const result = this.handlers[handlerName](...args);
      return result || {success: true};
    } catch (error) {
      console.error(`[ChattyJS] Error in ${handlerName}:`, error);
      return {success: false, error: error.message};
    }
  }

  #_checkIsOpen() {
    const tabInfo = getWidgetSettingsStorage();
    return tabInfo.open;
    // return document.querySelector('.Avada-Faqs_Button').classList.contains('opened');
  }
  #_clickWidgetTrigger() {
    const widgetTrigger = document.getElementById('Avada-FAQ_WidgetTrigger');
    if (!widgetTrigger) return;
    widgetTrigger.click();
  }

  #_openPage(page) {
    if (!this.hasHandler('openPage')) {
      console.warn('[ChattyJS] openPage handler not registered');
      return {success: false, error: 'Handler not available'};
    }
    try {
      const currentPageInfo = getCurrentTabInfo(page);
      const result = this.handlers.openPage(currentPageInfo.page);
      return result || {success: true};
    } catch (error) {
      console.error('[ChattyJS] Error in openPage:', error);
      return {success: false, error: error.message};
    }
  }

  openWidget(page = DEEP_LINK[0]) {
    if (!DEEP_LINK.includes(page)) return;
    if (!this.#_checkIsOpen()) this.#_clickWidgetTrigger();
    if (this.hasHandler('openPage')) return this.#_openPage(page);
    const currentPageInfo = getCurrentTabInfo(page);
    const isHomePage = currentPageInfo.page === 'homePage';
    const isHelpPage = currentPageInfo.page === 'categoriesList';
    setWidgetSettingsStorage({
      open: true,
      currentTab: currentPageInfo.tab,
      currentPage: currentPageInfo.page,
      history: ['homePage', !isHomePage && currentPageInfo.page].filter(Boolean),
      ...(!isHelpPage && {isExpand: false})
    });
  }

  closeWidget() {
    if (this.#_checkIsOpen()) this.#_clickWidgetTrigger();
    setWidgetSettingsStorage({open: false});
  }

  enableDebug() {
    this.debug = true;
  }

  disableDebug() {
    this.debug = false;
  }

  destroy() {
    // Clean up event listeners
    if (this.unsubscribeFromTool) {
      this.unsubscribeFromTool();
      this.unsubscribeFromTool = null;
    }
  }
}
